/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { BarElement, BarElementProps, BarElementSlotProps } from '@mui/x-charts';
import type { BarClasses } from '@mui/x-charts/BarChart';
import type { SeriesId } from '@mui/x-charts/models';

// BarElementOwnerState is not re-exported from the public API in x-charts v9.
// Inline the shape from barClasses.d.ts.
type BarElementOwnerState = {
  seriesId: SeriesId;
  dataIndex: number;
  color: string;
  isFaded: boolean;
  isHighlighted: boolean;
  isFocused: boolean;
  classes?: Partial<BarClasses>;
};

export type ProductUsageBreakdownChartBarProps = {
  ownerState: BarElementOwnerState;
  style: React.CSSProperties;
  topSeriesId?: string | number;
  bottomSeriesId?: string | number;
} & Omit<BarElementSlotProps['bar'], 'onClick'>;

const BAR_WIDTH = 17;
const CORNER_RADIUS = 4;

export const ProductUsageBreakdownChartBar: React.FC<ProductUsageBreakdownChartBarProps> = (props) => {
  const { ownerState, style, topSeriesId, bottomSeriesId, ...propsWithoutOwnerState } = props;
  const barProps = propsWithoutOwnerState as BarElementProps;

  const currentWidth = getCurrentWidth(style);

  const isTop = ownerState.seriesId === topSeriesId;
  const isBottom = ownerState.seriesId === bottomSeriesId;

  const clipPath = useMemo(() => {
    const insetAmount = currentWidth < BAR_WIDTH ? 0 : (currentWidth - BAR_WIDTH) / 2;
    const topRadius = isTop ? CORNER_RADIUS : 0;
    const bottomRadius = isBottom ? CORNER_RADIUS : 0;

    return `inset(0px ${insetAmount}px 0px ${insetAmount}px round ${topRadius}px ${topRadius}px ${bottomRadius}px ${bottomRadius}px)`;
  }, [currentWidth, isTop, isBottom]);

  return <BarElement {...ownerState} {...barProps} width={BAR_WIDTH} style={{ ...style, clipPath }} />;
};

function getCurrentWidth(style: React.CSSProperties): number {
  try {
    const widthPropertySymbol = Object.getOwnPropertySymbols(style.width).at(0) as symbol;
    const widthAsAny = style.width as any;

    return widthAsAny[widthPropertySymbol]();
  } catch {
    return 0;
  }
}
